import type { ComponentProps } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { faTwitter } from '@soundxyz/font-awesome/free-brands-svg-icons/index.mjs';
import { faCheck, faCode } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCopy } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import type { ShareModalSource } from '@soundxyz/utils/analytics';
import { NULL_ADDRESS } from '@soundxyz/utils/constants/constants';
import type { ReferralSource } from '@soundxyz/utils/constants/embeds';
import { BaseConstWebappUrl } from '@soundxyz/utils/constants/embeds';
import { styled, theme } from '@components-shared/src/stitches';
import { LearnMoreReferrals } from '@components/Referrals/LearnMore';
import { ShareReleaseImage } from '@components/Share/ShareReleaseImage';
import { Button } from '@components/main-components/button/Button';
import { DeprecatedTextInput } from '@components/main-components/forms/inputs/TextInput/TextInput';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMediaQuery } from '@context/mediaQuery';
import { ModalContext, ModalType } from '@context/modal';
import { ToastContext } from '@context/toast';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  PlaylistShareInfoFragmentDoc,
  ReleaseShareInfoDocument,
  ReleaseShareInfoFragmentDoc,
  useQuery,
} from '@graphql/reactQuery';
import { useCheckReferrableRelease } from '@hooks/useCheckReferrable';
import { useCopy } from '@hooks/useCopy';
import { usePrefetchEmbed } from '@hooks/usePrefetchEmbed';
import { useUrlWithAuthReferral } from '@hooks/useUrlWithReferral';
import { trackClickEvent } from '@lib/analytics';
import type { TopChartsType } from '@pages-shared/topCharts';
import { getUrlPathForTwitter } from '@utils/helpers';
import { KeepCurrentBrowserHost } from '@utils/url';
import { EmbedModal } from './EmbedModal';
import type { Percent } from './ShareModalHelpers';
import { defaultFeePercent, useDynamicFeePercent } from './ShareModalHelpers';

export const SHARE_AND_EARN_COPY = (fee: Percent) => `Share & Earn ${fee}` as const;

export const EARN_RELEASE_FEE_COPY = 'Earn curator rewards for each mint driven through your link';

const EARN_PLAYLIST_FEE_COPY = (percent: Percent) =>
  `Earn up to ${percent} for each primary sale generated by your playlist’s referral link.`;

/*#__PURE__*/ gql(/* GraphQL */ `
  query releaseShareInfo($id: UUID!) {
    release(id: $id) {
      id
      ...ReleaseShareInfo
    }
  }

  fragment ReleaseShareInfo on Release {
    id
    webappUri
    publicAffiliateFeePercent
    isFirstPhaseCompleted
    samConfig {
      id
      affiliateFeePercent
    }
    ...ReleaseReferrableInfo
    ...DynamicFeePercentInfo
  }

  fragment PlaylistShareInfo on Shelf {
    id
    webappUri
    user {
      id
    }
  }
`);

export type ShareModalProps = (
  | ComponentProps<typeof ShareModalRelease>
  | ComponentProps<typeof ShareModalPlaylist>
) & {
  /** Where in the UI this component is triggered from? This is helpful for analytics purposes */
  source: ShareModalSource;
};

export type ShareModalViralProps = ComponentProps<typeof ShareModalViral>;

const sourceLink: ReferralSource = {
  link: true,
};

export function ShareModalRelease({ releaseId }: { releaseId: string }) {
  const { tablet } = useMediaQuery();
  const { data } = useQuery(ReleaseShareInfoDocument, {
    variables: {
      id: releaseId,
    },
    staleTime: 0,
  });

  const { closeCancelModal } = ModalContext.useContainer();

  const release = data?.data.release;

  if (!release) {
    return (
      <StandardModal
        body={loadingBody}
        title="Share"
        closeModal={closeCancelModal}
        bodyWithoutPaddingBottom
        overlayClassName={clsx(!tablet && 'p-0')}
        isFullScreen={!tablet}
      />
    );
  }

  return <ShareModalReleaseInternal release={release} />;
}

const ShareButton = ({
  label,
  onClick,
  icon,
}: {
  label: string;
  onClick: () => void;
  icon: JSX.Element;
}) => {
  return (
    <Button
      variant="tertiary"
      label={label}
      className="px-[10px] py-3 font-base text-base-m font-semibold"
      leadingIcon={icon}
      onClick={onClick}
      size="M"
      fullWidth
    />
  );
};

function ShareModalReleaseInternal({
  release,
}: {
  release: FragmentType<ReleaseShareInfoFragmentDoc> & { id: string };
}) {
  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();
  const { closeCancelModal, openModal } = ModalContext.useContainer();
  const { openToast } = ToastContext.useContainer();
  const { tablet } = useMediaQuery();

  const releaseInfo = getFragment(ReleaseShareInfoFragmentDoc, release);

  const feePercent = useDynamicFeePercent({
    releaseInfo,
  });

  const { isShareReferrable } = useCheckReferrableRelease({
    release: releaseInfo,
  });

  const { urlWithReferral, copyUrlWithReferral, isCopied, referralAddress } =
    useUrlWithAuthReferral({
      url: releaseInfo.webappUri,
      source: sourceLink,
      isReferrable: isShareReferrable,
    });

  const onTwitterShareClick = () => {
    trackClickEvent({
      event: 'Share to Twitter Clicked',
      properties: {
        associationId: releaseInfo.id,
        associationType: 'Release',
        referralAddress: (referralAddress || NULL_ADDRESS).toLowerCase(),
      },
    });
    const path = getUrlPathForTwitter({ type: 'drop', url: urlWithReferral });
    window.open(path, '_newtab');
  };

  usePrefetchEmbed({
    releaseId: releaseInfo.id,
  });

  const onBackClick = () => {
    closeCancelModal();
    openModal(ModalType.SHARE, {
      body: <ShareModalRelease releaseId={release.id} />,
    });
  };

  const onEmbedClick = () => {
    closeCancelModal();
    trackClickEvent({
      event: 'Open Embeds Modal',
      properties: {
        associationId: releaseInfo.id,
        associationType: 'Release',
        referralAddress: (referralAddress || NULL_ADDRESS).toLowerCase(),
      },
    });
    openModal(ModalType.EMBED, {
      body: <EmbedModal type="drop" releaseId={releaseInfo.id} onBackClick={onBackClick} />,
    });
  };

  const onConnectClick = () => {
    trackClickEvent({
      event: 'Sign in Clicked',
      properties: { sourceComponent: 'Share Release Modal' },
    });
    closeCancelModal();
    login();
  };

  const copyIcon = isCopied ? (
    <FontAwesomeIcon icon={faCheck} color={theme.colors.brand600.toString()} />
  ) : (
    <FontAwesomeIcon icon={faCopy} />
  );

  const body = (
    <>
      <div className="grid grid-cols-3 gap-x-2 gap-y-4 lg:gap-y-6">
        <ShareReleaseImage
          releaseId={releaseInfo.id}
          onError={() =>
            openToast({
              text: 'There was an error downloading release screenshot',
              variant: 'error',
            })
          }
          className="col-span-3"
        />
        <ShareButton label="Copy Link" onClick={copyUrlWithReferral} icon={copyIcon} />
        <ShareButton
          label="Embed"
          icon={<FontAwesomeIcon icon={faCode} />}
          onClick={onEmbedClick}
        />
        <ShareButton
          label="Share"
          icon={<FontAwesomeIcon icon={faTwitter} />}
          onClick={onTwitterShareClick}
        />
      </div>
      {isShareReferrable && (
        <ShareInfo>
          <h4>{SHARE_AND_EARN_COPY(feePercent)}</h4>
          <ShareInfoFlexWrapper>
            <p>
              {EARN_RELEASE_FEE_COPY}
              {isAuthenticatedWithAPI && (
                <>
                  {' '}
                  <LearnMoreReferrals textColor="white" />
                </>
              )}
            </p>
            {!isAuthenticatedWithAPI && (
              <Button variant="secondary" size="S" label="Sign in" onClick={onConnectClick} />
            )}
          </ShareInfoFlexWrapper>
        </ShareInfo>
      )}
    </>
  );

  return (
    <StandardModal
      body={body}
      title="Share"
      closeModal={closeCancelModal}
      bodyClassName="pt-4"
      overlayClassName={clsx(!tablet && 'p-0')}
      isFullScreen={!tablet}
      bodyWithoutPaddingBottom
    />
  );
}

export function ShareModalPlaylist({
  playlist,
}: {
  playlist: FragmentType<PlaylistShareInfoFragmentDoc> & { id: string };
}) {
  const { isAuthenticatedWithAPI, authUser } = useAuth();
  const { login } = useConnectedAuth();
  const { closeCancelModal, openModal } = ModalContext.useContainer();

  const playlistInfo = getFragment(PlaylistShareInfoFragmentDoc, playlist);

  const playlistUrl = KeepCurrentBrowserHost(playlistInfo.webappUri).href;

  const { copy: copyPlaylistUrl, isCopied } = useCopy({
    text: playlistUrl,
    successMessage: 'Link copied to clipboard',
  });

  usePrefetchEmbed({
    playlistId: playlistInfo.id,
  });

  const onTwitterShareClick = () => {
    const path = getUrlPathForTwitter({ type: 'playlist', url: playlistUrl });
    window.open(path, '_newtab');
  };

  const onBackClick = () => {
    closeCancelModal();
    openModal(ModalType.SHARE, {
      body: <ShareModalPlaylist playlist={playlist} />,
    });
  };

  const onEmbedClick = () => {
    closeCancelModal();

    openModal(ModalType.EMBED, {
      body: <EmbedModal type="playlist" playlistId={playlistInfo.id} onBackClick={onBackClick} />,
    });
  };

  const onConnectClick = () => {
    closeCancelModal();
    login();
  };

  const body = (
    <>
      <InputWrapper onClick={copyPlaylistUrl}>
        <DeprecatedTextInput disabled value={playlistUrl} containerStyles={{ width: '100%' }} />
        <StyledIconButton
          variant="tertiary"
          iconSize={16}
          size={24}
          icon={
            isCopied ? (
              <FontAwesomeIcon icon={faCheck} color={theme.colors.brand600.toString()} />
            ) : (
              <FontAwesomeIcon icon={faCopy} />
            )
          }
        />
      </InputWrapper>
      <ButtonWrapper>
        <Button
          variant="tertiary"
          label="Share to Twitter"
          leadingIcon={<FontAwesomeIcon icon={faTwitter} />}
          onClick={onTwitterShareClick}
          size="M"
          fullWidth
        />
        <Button
          variant="tertiary"
          label="Embed"
          leadingIcon={<FontAwesomeIcon icon={faCode} />}
          onClick={onEmbedClick}
          size="M"
          fullWidth
        />
      </ButtonWrapper>
      {authUser?.id === playlistInfo.user.id && (
        <ShareInfo>
          <h4>{SHARE_AND_EARN_COPY(defaultFeePercent)}</h4>
          <ShareInfoFlexWrapper>
            <p>
              {EARN_PLAYLIST_FEE_COPY(defaultFeePercent)}
              {isAuthenticatedWithAPI && (
                <>
                  {' '}
                  <LearnMoreReferrals textColor="white" />
                </>
              )}
            </p>
            {!isAuthenticatedWithAPI && (
              <Button variant="secondary" size="S" label="Sign in" onClick={onConnectClick} />
            )}
          </ShareInfoFlexWrapper>
        </ShareInfo>
      )}
    </>
  );

  return (
    <StandardModal
      body={body}
      title="Share"
      closeModal={closeCancelModal}
      bodyWithoutPaddingBottom
    />
  );
}

export function ShareModalViral() {
  const { closeCancelModal } = ModalContext.useContainer();
  const { pathname } = useRouter();

  const url = `${BaseConstWebappUrl}${pathname?.substring(1)}`;

  const { copy, isCopied } = useCopy({
    text: url,
    successMessage: 'Link copied to clipboard',
  });

  const onTwitterShareClick = () => {
    const path = getUrlPathForTwitter({ type: 'viral', url });
    window.open(path, '_newtab');
  };

  const body = (
    <>
      <InputWrapper onClick={copy}>
        <DeprecatedTextInput disabled value={url} containerStyles={{ width: '100%' }} />
        <StyledIconButton
          variant="tertiary"
          iconSize={16}
          size={24}
          icon={
            isCopied ? (
              <FontAwesomeIcon icon={faCheck} color={theme.colors.brand600.toString()} />
            ) : (
              <FontAwesomeIcon icon={faCopy} />
            )
          }
        />
      </InputWrapper>
      <ButtonWrapper>
        <Button
          variant="tertiary"
          label="Share to Twitter"
          leadingIcon={<FontAwesomeIcon icon={faTwitter} />}
          onClick={onTwitterShareClick}
          size="M"
          fullWidth
        />
      </ButtonWrapper>
    </>
  );

  return (
    <StandardModal
      body={body}
      title="Share"
      closeModal={closeCancelModal}
      bodyWithoutPaddingBottom
    />
  );
}

export function ShareModalTop({ type }: { type: TopChartsType }) {
  const { closeCancelModal } = ModalContext.useContainer();

  const url = typeof window !== 'undefined' ? window.location.href : '';

  const { copy, isCopied } = useCopy({
    text: url,
    successMessage: 'Link copied to clipboard',
  });

  const onTwitterShareClick = () => {
    const path = getUrlPathForTwitter({ type: 'top', subType: type, url });
    window.open(path, '_newtab');
  };

  const body = (
    <>
      <InputWrapper onClick={copy}>
        <DeprecatedTextInput disabled value={url} containerStyles={{ width: '100%' }} />
        <StyledIconButton
          variant="tertiary"
          iconSize={16}
          size={24}
          icon={
            isCopied ? (
              <FontAwesomeIcon icon={faCheck} color={theme.colors.brand600.toString()} />
            ) : (
              <FontAwesomeIcon icon={faCopy} />
            )
          }
        />
      </InputWrapper>
      <ButtonWrapper>
        <Button
          variant="tertiary"
          label="Share to Twitter"
          leadingIcon={<FontAwesomeIcon icon={faTwitter} />}
          onClick={onTwitterShareClick}
          size="M"
          fullWidth
        />
      </ButtonWrapper>
    </>
  );

  return (
    <StandardModal
      body={body}
      title="Share"
      closeModal={closeCancelModal}
      bodyWithoutPaddingBottom
    />
  );
}

const StyledIconButton = styled(IconButton, {
  width: 40,
  height: 44,
  flexShrink: 0,
  borderRadius: 0,
  transition: '0.2s',
});

const InputWrapper = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid $neutral100',
  borderRadius: 8,
  marginBottom: 16,
  overflow: 'hidden',

  input: {
    height: 44,
    width: '100%',
    borderRadius: 0,

    '&:disabled': {
      color: '$black',
    },
  },

  button: {
    width: 40,
    height: 44,
    flexShrink: 0,
    borderRadius: 0,
  },
});

const ButtonWrapper = styled('div', {
  display: 'flex',
  gap: 16,
});

const ShareInfo = styled('div', {
  backgroundColor: '$base900',
  marginTop: 16,
  padding: '16px 12px',
  borderRadius: 8,

  h4: {
    base: 'L',
    fontStyle: 'semibold',
    color: '$white',
    marginBottom: 4,
  },

  p: {
    base: 'M',
    color: '$neutral400',
    fontStyle: 'medium',
  },

  button: {
    padding: '9px 12px',
  },

  a: {
    color: '$white',
    textDecoration: 'underline',
  },
});

const ShareInfoFlexWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex justify-between gap-2">{children}</div>;
};

const loadingBody = (
  <div className="grid grid-cols-3 gap-x-2 gap-y-4 lg:gap-y-6">
    <ShareReleaseImage className="col-span-3" releaseId="" isLoading />
    <Button
      variant="tertiary"
      label="Copy Link"
      leadingIcon={<FontAwesomeIcon icon={faCopy} />}
      disabled
      size="M"
      fullWidth
    />
    <Button
      variant="tertiary"
      label="Share"
      leadingIcon={<FontAwesomeIcon icon={faTwitter} />}
      disabled
      size="M"
      fullWidth
    />
    <Button
      variant="tertiary"
      label="Embed"
      leadingIcon={<FontAwesomeIcon icon={faCode} />}
      disabled
      size="M"
      fullWidth
    />
  </div>
);
